'use client';
import { useDerivedState } from '@repo/ui/src/hooks/useDerivedState';
import { classNames } from '@repo/ui/src/utils/classNames';
import React, { useCallback } from 'react';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';

export type HandleChange = (newQuantity: number) => void;

interface QuantitySelectorProps {
  handleChange: HandleChange;
  quantity: number;
  max?: number;
  min?: number;
  className?: string;
  size?: 'small' | 'medium' | 'large';
}

const QuantitySelector = ({
  quantity,
  handleChange,
  max = 100,
  min = 1,
  className,
  size = 'small',
}: QuantitySelectorProps) => {
  const [internalQuantity, setInternalQuantity] = useDerivedState(quantity);
  const disableMax = internalQuantity === max;
  const disableMin = internalQuantity === min;
  const { t } = useTranslation();

  const handleIncrement = useCallback(() => {
    setInternalQuantity((currValue: number) => {
      const newQuantity = currValue < max ? currValue + 1 : max;
      handleChange(newQuantity);
      return newQuantity;
    });
  }, [setInternalQuantity, handleChange, max]);

  const handleDecrement = useCallback(() => {
    setInternalQuantity((currValue: number) => {
      const newQuantity = currValue ? currValue - 1 : min;
      handleChange(newQuantity);
      return newQuantity;
    });
  }, [setInternalQuantity, handleChange, min]);

  const handleInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInternalQuantity((currValue: number) => {
        const val = parseInt(event.target.value, 10);
        const newQuantity = Number.isNaN(val) ? currValue : val;
        if (newQuantity <= max && newQuantity >= min) {
          handleChange(newQuantity);
          return newQuantity;
        }
        if (newQuantity > max) {
          handleChange(max);
          return max;
        }
        handleChange(min);
        return min;
      });
    },
    [setInternalQuantity, handleChange, max, min],
  );

  const sizeClasses = {
    small: {
      button: 'w-6 h-6',
      input: 'w-8 h-6',
      text: 'tra-text-web-body-m',
    },
    medium: {
      button: 'w-10 h-10',
      input: 'w-10 h-10',
      text: 'text-base leading-6',
    },
    large: {
      button: 'w-10 h-10',
      input: 'w-10 h-10',
      text: 'text-base leading-6',
    },
  };

  const currentSize = sizeClasses[size];

  return (
    <div className={classNames('flex', className)}>
      <button
        className={classNames(
          'disabled:cursor-not-allowed border border-neutral-300 border-solid',
          currentSize.button,
        )}
        onClick={handleDecrement}
        aria-label={t('cart.lineItemText.removeText', undefined, { default: 'decrease' })}
        data-testid="c-decrement-btn"
        disabled={disableMin}
      >
        <span className={classNames(disableMin && 'text-neutral-200', 'h-full', currentSize.text)}>
          -
        </span>
      </button>
      <input
        className={classNames(
          'hideArrows [appearance:textfield] border-t border-b border-neutral-300 bg-white block text-center rounded-none font-bold',
          currentSize.input,
          currentSize.text,
        )}
        value={internalQuantity}
        aria-label={`Quantity ${internalQuantity}`}
        onChange={handleInput}
        type="number"
        max={max}
        min={min}
      />

      <button
        className={classNames(
          'disabled:cursor-not-allowed border border-neutral-300 border-solid',
          currentSize.button,
        )}
        onClick={handleIncrement}
        aria-label={t('cart.lineItemText.addText', undefined, { default: 'increase' })}
        data-testid="c-increment-btn"
        disabled={disableMax}
      >
        <span className={classNames(disableMax && 'text-neutral-200', 'h-full', currentSize.text)}>
          +
        </span>
      </button>
    </div>
  );
};

export default QuantitySelector;
